import { useSelector } from "react-redux"
import _ from "lodash"
import { isString } from "../../commons/utils"
import { COUNTING_IGNORED_ELEMENT_TYPES } from "../admin/forms/AdminFormCreationAddableElements"
import {
  findQuestionNumberForElement,
  getOffset,
  transformRowsIdsToRowsElements
} from "./formUtils"

/**
 * Form controller manage current state of form.
 * Handle properly notification to view when modification are made.
 */
export default class FormController {
  /**
   *
   * @param form : any - current edited form, it should be a React Ref.
   * @param notifyChange : function - a function to update component React state
   * @param isAdminMode : boolean - just a flag to know if it's an Admin mode
   */
  constructor(form, notifyChange = _.noop, isAdminMode = false) {
    if (form) {
      this.form = {
        // Here we set every field one by one, to respect the model (see webportal-models/models/form.js), because we might add 'ghost' fields
        _id: form._id,
        name: form.name,
        formIdentifier: form.formIdentifier,
        signable: form.signable,
        strict: form.strict,
        type: form.type,
        frequency: form.frequency,
        elements: form.elements,
        rows: form.rows,
        pages: form.pages
      }
    }

    this.isAdminMode = isAdminMode
    this.notifyChange = notifyChange
    this.attributes = [] // All attributes available for the current form - will be updated every time an element is created
    this.tags = [] // All tags available for the tags List element, created here for preventing lazy fetching

    if (isAdminMode) {
      this.attributes = useSelector((state) => state.formSlice.attributes) // eslint-disable-line react-hooks/rules-of-hooks
      this.tags = useSelector((state) => state.formSlice.tags) // eslint-disable-line react-hooks/rules-of-hooks
    }
  }

  updateForm(form) {
    this.form = form || this.form
    this.notifyChange(this.form)
  }

  isPersistent() {
    return this.form.type === "persistent"
  }

  name() {
    return this.form.name
  }

  signable() {
    return this.form.signable
  }

  strict() {
    return this.form.strict || false
  }

  elements() {
    return this.form.elements
  }

  /* Here, 'row' should be an array of strings (i.e. `["text-1", "address-1"])` */
  elementsOfRow(row) {
    return this.form.elements.filter((e) => row.includes(e.id))
  }

  formElementsMap() {
    const elements = this.form.elements || []
    return elements.reduce((acc, elem) => {
      acc[elem.id] = elem
      return acc
    }, {})
  }

  rows(asElements = false) {
    if (!asElements) {
      return this.form.rows
    }
    const formElementsMap = this.formElementsMap()
    return transformRowsIdsToRowsElements(this.form.rows, formElementsMap)
  }

  rowsOfPage(pageIndex, asElements = false) {
    const elementsIds = this.elementsIdsOfPage(pageIndex)
    const rows = this.form.rows.reduce((acc, row) => {
      if (isString(row) && elementsIds.includes(row)) {
        const elemId = row
        acc.push([elemId])
      }
      if (Array.isArray(row)) {
        acc.push(row.filter((elemId) => elementsIds.includes(elemId)))
      }
      return acc
    }, [])

    if (!asElements) {
      return rows
    }
    const formElementsMap = this.formElementsMap()
    return transformRowsIdsToRowsElements(rows, formElementsMap)
  }

  findQuestionNumberForElement(element, rowIndex, rows) {
    const formRows = rows || this.rows(true)
    const offset = getOffset(formRows, rowIndex)
    return findQuestionNumberForElement(formRows[rowIndex], element, offset)
  }

  pages() {
    return this.form.pages || []
  }

  isPaginated() {
    return this.pages().length > 0
  }

  elementsIdsOfPage(index) {
    if (this.pages().length <= index) {
      return []
    }
    const page = this.form.pages[index]
    return page.elementsIds
  }

  elementsOfPage(index) {
    if (this.pages().length <= index) {
      return []
    }
    const formElements = this.formElementsMap()
    const page = this.form.pages[index]
    return page.elementsIds.map((elemId) => formElements[elemId])
  }

  /**
   * This functions returns an object with the shown number for every element of the form (for CLIENTS)
   * i.e. : { "text-1": 0, "address-1": 2, "text-2": 3, "phone-1": 1 }
   * TODO: Je pense qu'on peut améliorer la logique de cette fonction. C'est un peu compliqué pour rien et il y a beaucoup d'itérations
   * */
  elementsNumbers() {
    const pages = this.pages()
    const elementsNumbers = {}
    let nb = 1
    const rows = this.isPaginated()
      ? pages.reduce((acc, ignored, pageIdx) => {
          acc.push(...this.rowsOfPage(pageIdx))
          return acc
        }, [])
      : this.rows()
    const formElementsMap = this.formElementsMap()
    const rowsElements = transformRowsIdsToRowsElements(rows, formElementsMap)

    rowsElements.map((rowElements) =>
      rowElements.map((element) => {
        const ignored = COUNTING_IGNORED_ELEMENT_TYPES.includes(element.type)
        elementsNumbers[element.id] = ignored ? undefined : nb++
        return element
      })
    )
    return elementsNumbers
  }
}
